import { getIntegrations, GWS, OFFICE365 } from '../../../crud/integration.crud'

export const INTEGRATION_APPS = {
  OFFICE_365: 'Office365',
  GWS: 'GWS',
  DUO_SECURITY: 'DUOSecurity',
  AUTOTASK: 'Autotask',
  SYNCRO: 'Syncro',
  CONNECTWISE: 'ConnectWise',
  HALO: 'Halo',
}

export const DIRECTORY_INTEGRATION_PREFERENCE = [OFFICE365, GWS]
export const DIRECTORY_SYNC_SUCCEEDED = 'SyncSucceeded'

export const getIntegrationApplicationName = (integration) => integration?.IntegrationApplication?.application_name

const unwrapCustomerId = (value) => {
  if (value != null && typeof value === 'object') {
    return value.id ?? value.customer_id
  }
  return value
}

export const getIntegrationCustomerId = (integration) =>
  unwrapCustomerId(integration?.customer ?? integration?.customer_id ?? integration?.Customer)

export const isOwnCustomerIntegration = (integration, customerId) =>
  integration != null && customerId != null && Number(getIntegrationCustomerId(integration)) === Number(customerId)

export const isParentCspMappedIntegration = (integration, customerId) =>
  customerId != null &&
  Number(integration?.mappedCspRequester) === Number(customerId) &&
  !isOwnCustomerIntegration(integration, customerId)

/**
 * Same source as the Integrations page: GET /customer/:id/integration.
 * Google is present if a GWS row is in that list. Microsoft is this company's
 * integration only when the row's customer id matches (parent CSP mapping does not).
 */
export const getConnectedDirectoryIntegrations = (integrations = [], customerId) => {
  const list = Array.isArray(integrations) ? integrations : []
  const gws = list.find((item) => getIntegrationApplicationName(item) === GWS)
  const office365 = list.find((item) => {
    if (getIntegrationApplicationName(item) !== OFFICE365) {
      return false
    }
    if (customerId == null) {
      return true
    }
    if (Number(item.mappedCspRequester) === Number(customerId)) {
      return false
    }
    return Number(getIntegrationCustomerId(item)) === Number(customerId)
  })

  return { gws, office365 }
}

export const integrationsListBelongsToCustomer = (integrations, customerId) => {
  if (customerId == null) {
    return false
  }

  return (Array.isArray(integrations) ? integrations : []).some(
    (item) => isOwnCustomerIntegration(item, customerId) || isParentCspMappedIntegration(item, customerId),
  )
}

export const getPreferredDirectoryIntegration = (
  integrations = [],
  preference = DIRECTORY_INTEGRATION_PREFERENCE,
  { customerId } = {},
) => {
  const list = Array.isArray(integrations) ? integrations : []
  const { gws, office365 } = getConnectedDirectoryIntegrations(list, customerId)
  const usableOffice365 =
    office365 &&
    (gws == null || office365.syncStatus === undefined || office365.syncStatus === DIRECTORY_SYNC_SUCCEEDED)
      ? office365
      : undefined
  const connectedByName = {
    [OFFICE365]: usableOffice365,
    [GWS]: gws,
  }
  const preferredName = preference.find((appName) => connectedByName[appName])
  if (preferredName) {
    return connectedByName[preferredName]
  }

  // CSP-mapped Microsoft child with no Google: the Office365 row belongs to the parent.
  if (gws == null) {
    return list.find((item) => getIntegrationApplicationName(item) === OFFICE365)
  }
  return undefined
}

export const getPreferredDirectoryIntegrationName = ({ hasOffice365 = false, hasGws = false } = {}) => {
  if (hasOffice365) {
    return OFFICE365
  }
  if (hasGws) {
    return GWS
  }
  return undefined
}

export const getPreferredDirectoryApplicationName = (
  applicationNames = [],
  preference = DIRECTORY_INTEGRATION_PREFERENCE,
) => {
  const names = Array.isArray(applicationNames) ? applicationNames : []
  return preference.find((appName) => names.includes(appName))
}

export const getDirectoryApplicationNamesForDefault = (selectedCustomer) => {
  const applicationNames = selectedCustomer?.integratedApplications ?? []
  // CSP mapping injects Office365 onto children that only have Google. Engage should not treat that as an M365 tenant.
  if (selectedCustomer?.hasCspMapping && !selectedCustomer?.hasOwnCSPIntegration) {
    return applicationNames.filter((name) => name !== OFFICE365)
  }
  return applicationNames
}

export const isSelectedDirectoryIntegrationValid = ({ selectedIntegration, integrations = [], customerId } = {}) => {
  if (selectedIntegration?.id == null) {
    return false
  }
  const { gws, office365 } = getConnectedDirectoryIntegrations(integrations, customerId)
  if (selectedIntegration.id === gws?.id) {
    return true
  }
  if (selectedIntegration.id !== office365?.id) {
    return false
  }
  const preferred = getPreferredDirectoryIntegration(integrations, undefined, { customerId })
  return preferred?.id === office365?.id
}

export const selectDirectoryIntegration = ({
  selectedIntegration,
  integrations = [],
  customerId,
  preference = DIRECTORY_INTEGRATION_PREFERENCE,
} = {}) => {
  if (isSelectedDirectoryIntegrationValid({ selectedIntegration, integrations, customerId })) {
    return selectedIntegration
  }
  return getPreferredDirectoryIntegration(integrations, preference, { customerId })
}

const isOwnedByAnotherCustomer = (integration, customerId) => {
  if (customerId == null || integration == null) {
    return false
  }
  const ownerId = getIntegrationCustomerId(integration)
  if (ownerId == null) {
    return false
  }
  return Number(ownerId) !== Number(customerId) && !isParentCspMappedIntegration(integration, customerId)
}

const findDirectoryIntegrationByNameForCustomer = (integrations, customerId, applicationName) => {
  const list = Array.isArray(integrations) ? integrations : []
  const matchesApplication = (item) => getIntegrationApplicationName(item) === applicationName

  if (customerId == null) {
    return list.find(matchesApplication)
  }

  const ownMatch = list.find((item) => matchesApplication(item) && isOwnCustomerIntegration(item, customerId))
  if (ownMatch) {
    return ownMatch
  }

  if (applicationName === OFFICE365) {
    const parentCspMatch = list.find(
      (item) => matchesApplication(item) && isParentCspMappedIntegration(item, customerId),
    )
    if (parentCspMatch) {
      return parentCspMatch
    }
  }

  return list.find((item) => matchesApplication(item) && getIntegrationCustomerId(item) == null)
}

/**
 * Directory provider for the employee details modal. Prefer the opening page's selection
 * (Employees keeps GWS/M365 in local hook state). Do not write Redux here — Engage's table
 * selection must not be overwritten when this modal opens.
 */
export const resolveDirectoryIntegrationForModal = ({
  selectedIntegration: selectedIntegrationFromPage,
  selectedIntegrationType,
  reduxSelectedIntegration,
  integrations = [],
  customerId,
} = {}) => {
  if (getIntegrationApplicationName(selectedIntegrationFromPage)) {
    return selectedIntegrationFromPage
  }

  const list = Array.isArray(integrations) ? integrations : []

  if (selectedIntegrationType) {
    const fromThisCustomer = findDirectoryIntegrationByNameForCustomer(list, customerId, selectedIntegrationType)
    if (fromThisCustomer) {
      return fromThisCustomer
    }

    if (
      getIntegrationApplicationName(reduxSelectedIntegration) === selectedIntegrationType &&
      !isOwnedByAnotherCustomer(reduxSelectedIntegration, customerId)
    ) {
      return reduxSelectedIntegration
    }

    return { IntegrationApplication: { application_name: selectedIntegrationType } }
  }

  if (
    isSelectedDirectoryIntegrationValid({
      selectedIntegration: reduxSelectedIntegration,
      integrations,
      customerId,
    })
  ) {
    return reduxSelectedIntegration
  }

  return (
    selectDirectoryIntegration({
      selectedIntegration: reduxSelectedIntegration,
      integrations,
      customerId,
    }) ?? reduxSelectedIntegration
  )
}

export const INTEGRATION_TYPE = {
  LEGACY: 0, // deprecated powershell credential authentication
  TOKEN: 1, // token based authentication
  APP: 2, // 1 to 1 application to integration model
  CSP: 3, // CSP multi-tenant single app model
}

export const getM365CspIntegration = async ({ parentId }) => {
  let cspIntegration
  try {
    const parentIntegrations = await getIntegrations(parentId)
    cspIntegration = parentIntegrations?.data?.find(
      (integration) =>
        integration?.IntegrationApplication?.application_name === 'Office365' &&
        integration?.integration_type === INTEGRATION_TYPE.CSP,
    )
  } catch (error) {
    console.error('Failed to retrieve parent company integrations', error)
  }

  return cspIntegration ?? null
}

export const PSA_INTEGRATIONS = ['Autotask', 'Halo', 'Syncro', 'ConnectWise']
